import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  getAgentTiers,
  GetAgentTiersRequest,
  AgentTierData,
  deleteAgentTier,
} from '@/application/requests/agentTier';

export interface DistributorState {
  distributors: AgentTierData[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
  // Delete state
  deleteLoading: boolean;
  deleteError: string | null;
  // All distributors for dropdown (loaded once with limit 1000)
  allDistributors: AgentTierData[];
  allDistributorsLoading: boolean;
  allDistributorsLoaded: boolean;
  allDistributorsError: string | null;
}

const initialState: DistributorState = {
  distributors: [],
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  pageSize: 10,
  hasNext: false,
  hasPrevious: false,
  deleteLoading: false,
  deleteError: null,
  // All distributors
  allDistributors: [],
  allDistributorsLoading: false,
  allDistributorsLoaded: false,
  allDistributorsError: null,
};

export const fetchDistributors = createAsyncThunk(
  'distributor/fetchDistributors',
  async (params: GetAgentTiersRequest, { rejectWithValue }) => {
    try {
      const response = await getAgentTiers(params);

      if (!response.success) {
        return rejectWithValue(
          response.message || 'Failed to fetch distributors'
        );
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch distributors');
    }
  }
);

export const removeDistributor = createAsyncThunk(
  'distributor/removeDistributor',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await deleteAgentTier(id);

      if (!response.success) {
        return rejectWithValue(
          response.message || 'Failed to delete distributor'
        );
      }

      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete distributor');
    }
  }
);

export const fetchAllDistributors = createAsyncThunk(
  'distributor/fetchAllDistributors',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as { distributor: DistributorState };

    // If already loaded, don't fetch again
    if (state.distributor.allDistributorsLoaded) {
      return state.distributor.allDistributors;
    }

    try {
      const response = await getAgentTiers({ page: 1, limit: 1000 });

      if (!response.success) {
        return rejectWithValue(
          response.message || 'Failed to fetch all distributors'
        );
      }

      return response.data?.data || [];
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch all distributors'
      );
    }
  }
);

const distributorSlice = createSlice({
  name: 'distributor',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1; // Reset to first page when changing page size
    },
    clearError: (state) => {
      state.error = null;
      state.deleteError = null;
      state.allDistributorsError = null;
    },
    resetDistributors: () => initialState,
    // Add distributor to allDistributors list
    addToAllDistributors: (state, action: PayloadAction<AgentTierData>) => {
      if (state.allDistributorsLoaded) {
        state.allDistributors = [action.payload, ...state.allDistributors];
      }
    },
    // Update distributor in allDistributors list
    updateInAllDistributors: (state, action: PayloadAction<AgentTierData>) => {
      if (state.allDistributorsLoaded) {
        const index = state.allDistributors.findIndex(
          (d) => d.id === action.payload.id
        );
        if (index !== -1) {
          state.allDistributors[index] = action.payload;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch distributors
      .addCase(fetchDistributors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDistributors.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.distributors = action.payload.data;
          state.totalPages = action.payload.total_pages;
          state.totalItems = action.payload.total;
          state.currentPage = action.payload.page;
          state.pageSize = action.payload.limit;
          state.hasNext = action.payload.has_next;
          state.hasPrevious = action.payload.has_previous;
        }
      })
      .addCase(fetchDistributors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete distributor
      .addCase(removeDistributor.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(removeDistributor.fulfilled, (state, action) => {
        state.deleteLoading = false;
        // Remove the deleted distributor from the list
        state.distributors = state.distributors.filter(
          (d) => d.id !== action.payload
        );
        state.totalItems = Math.max(0, state.totalItems - 1);
        // Also remove from allDistributors
        if (state.allDistributorsLoaded) {
          state.allDistributors = state.allDistributors.filter(
            (d) => d.id !== action.payload
          );
        }
      })
      .addCase(removeDistributor.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload as string;
      })
      // Fetch all distributors
      .addCase(fetchAllDistributors.pending, (state) => {
        if (!state.allDistributorsLoaded) {
          state.allDistributorsLoading = true;
          state.allDistributorsError = null;
        }
      })
      .addCase(fetchAllDistributors.fulfilled, (state, action) => {
        state.allDistributorsLoading = false;
        state.allDistributors = action.payload;
        state.allDistributorsLoaded = true;
      })
      .addCase(fetchAllDistributors.rejected, (state, action) => {
        state.allDistributorsLoading = false;
        state.allDistributorsError = action.payload as string;
      });
  },
});

export const {
  setCurrentPage,
  setPageSize,
  clearError,
  resetDistributors,
  addToAllDistributors,
  updateInAllDistributors,
} = distributorSlice.actions;

export default distributorSlice.reducer;
